import type { PropsWithChildren } from "react";
import { Component } from "react";
import { captureException } from "@/utils/capture-exception";
import { isChunkLoadError, isUpdateError } from "@/utils/new-version";

import { ErrorUpdateMessage } from "./error-update-message";

// A one-off error, such as a translator or extension changing the DOM under
// React, usually goes away on a fresh mount. A second error this soon after a
// remount stays on the error page.
const remountWindowMs = 10_000;

export class ErrorBoundary extends Component<
  PropsWithChildren,
  { hasError: boolean; isUpdate: boolean; isChunkFailure: boolean }
> {
  lastRemountAt = 0;

  constructor(props: PropsWithChildren) {
    super(props);
    this.state = { hasError: false, isUpdate: false, isChunkFailure: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error(error);
    return {
      hasError: true,
      isUpdate: isUpdateError(error),
      isChunkFailure: isChunkLoadError(error),
    };
  }

  // Errors caught here never reach Sentry's global handlers, so real crashes are
  // reported, including the ones a remount recovers from. A chunk that failed to load
  // was reported where it failed, and a remount can't bring it back.
  componentDidCatch(error: Error) {
    if (this.state.isChunkFailure) {
      return;
    }

    captureException(error);

    // Runs in the commit phase, so React remounts before the error page paints.
    const now = Date.now();
    if (now - this.lastRemountAt > remountWindowMs) {
      this.lastRemountAt = now;
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorUpdateMessage
          isUpdate={this.state.isUpdate}
          isChunkFailure={this.state.isChunkFailure}
        />
      );
    }

    return this.props.children;
  }
}
